import { useState, useEffect, useMemo } from "react";
import { X, Search, ChevronLeft } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
//  Kennisbank — bron voor de in-app hulp. Kort en feitelijk gehouden.
//  Bij twijfel over HACCP-regels geldt altijd de officiële Hygiënecode.
// ─────────────────────────────────────────────────────────────────────────────
const HELP_CONTENT = [
  { cat: "Aan de slag", artikelen: [
    { titel: "Wat is KitchenControl?", tekst:
"KitchenControl brengt je dagelijkse keukentaken samen op één plek: mise en place, HACCP, recepten, team en het Whiteboard. De app werkt in de browser op telefoon, tablet en laptop. Gegevens worden per bedrijf in de cloud bewaard en tussen apparaten gesynchroniseerd." },
    { titel: "Het beginscherm gebruiken", tekst:
"Na het inloggen kom je op het dashboard. Daar zie je in één oogopslag de belangrijkste onderdelen, zoals de voortgang van Mise en place, HACCP, Recepten, Team en het Whiteboard. Tik op een onderdeel om het te openen." },
    { titel: "De navigatie gebruiken", tekst:
"De vaste navigatie geeft toegang tot Mise en Place, Recepten, HACCP, Team en Instellingen. Het vraagteken opent deze hulp. Het Whiteboard is ook vanaf het dashboard en vanuit relevante onderdelen bereikbaar." },
    { titel: "Werken op meerdere apparaten", tekst:
"KitchenControl is een web-app. Je kunt dezelfde bedrijfsomgeving op tablet, telefoon en computer gebruiken. Wijzigingen worden gesynchroniseerd. Zie je op een ander apparaat nog oude informatie, ververs dan de pagina zodat de nieuwste gegevens worden opgehaald." },
  ]},

  { cat: "Mise en place", artikelen: [
    { titel: "De dag- en weeklijst gebruiken", tekst:
"1. Open Mise en Place.\n2. Kies de gewenste week en dag.\n3. Gebruik Werkoverzicht voor de dagelijkse productie of Volledige lijst om de planning aan te vullen.\n4. Vink een taak af wanneer deze klaar is. De voortgang wordt direct bijgewerkt." },
    { titel: "Een taak afronden en een etiket printen", tekst:
"Wanneer je een geplande MEP-taak als klaar afvinkt, vraagt KitchenControl of je een etiket wilt printen. Kies Print etiket om direct de printflow te openen, of Nee, klaar als er geen etiket nodig is. In de printflow kies je daarna de bewaarstatus: Koeling, Vriezer of Uit vriezer. Koeling gebruikt de normale houdbaarheid; Vriezer maakt een vriesetiket; Uit vriezer maakt een etiket voor een product dat is ontdooid. Hiervoor is geen beheer-pincode nodig." },
    { titel: "Een bestaand MEP-item printen", tekst:
"Tik op de naam van een MEP-taak om direct het etiketscherm te openen. Dit is dagelijks gebruik en vraagt geen beheer-pincode. Het potlood is bedoeld voor het bewerken van het item en is een beheerfunctie." },
    { titel: "Een nieuw MEP-item toevoegen", tekst:
"Open de volledige MEP-lijst en kies Nieuw item. Vul minimaal de naam in en kies een afdeling en categorie. In hetzelfde scherm kun je ook direct een nieuwe afdeling of categorie aanmaken. Daarna kun je eventueel een recept koppelen, allergenen en houdbaarheid instellen en bestelartikelen toevoegen. Opslaan en beheren zijn beheerfuncties." },
    { titel: "Afdelingen en categorieën", tekst:
"Afdelingen en categorieën ordenen de MEP-lijst. Bij het toevoegen of bewerken van een item kun je een bestaande afdeling/categorie kiezen of direct een nieuwe aanmaken. Een nieuwe categorie wordt gekoppeld aan de gekozen afdeling." },
    { titel: "Bestelartikelen bij een MEP-item", tekst:
"Bestelartikelen zijn bedoeld voor producten of ingrediënten die je niet standaard op voorraad hebt, maar die wel nodig zijn wanneer een bepaald MEP-item wordt gemaakt. Bij een MEP-item kun je daarom vastleggen wat er besteld moet worden, met naam, hoeveelheid, eenheid en leverancier. Wanneer zo'n MEP-item in de planning staat, worden de relevante bestelartikelen automatisch zichtbaar op het Whiteboard. Zo zie je op tijd wat nog ingekocht moet worden en is er geen aparte Bestelbon nodig." },
    { titel: "Items archiveren", tekst:
"MEP-items die je niet meer dagelijks gebruikt kun je archiveren. Via Archief kun je ze later terugvinden en herstellen. Archiveren, herstellen en definitief verwijderen zijn beheerfuncties." },
    { titel: "Een taak naar morgen schuiven", tekst:
"Bij een geplande of afgeronde taak kun je Morgen kiezen. De taak wordt dan doorgeschoven naar de volgende dag. Omdat dit geen afronding voor etiketteren is, verschijnt daarbij geen vraag om een etiket te printen." },
  ]},

  { cat: "Etiketten printen", artikelen: [
    { titel: "De printflow", tekst:
"In het etiketscherm kies je de medewerker, de bewaarstatus en het aantal etiketten. Daarna kies je Print etiket. De printerstatus staat bovenaan het venster." },
    { titel: "Koeling, Vriezer en Uit vriezer", tekst:
"Koeling is de standaard en gebruikt de normale houdbaarheid van het MEP-item of gekoppelde recept.\n\nVriezer berekent de THT voorlopig 6 kalendermaanden vooruit en toont INGEVROREN en 'Bewaren bij -18°C'.\n\nUit vriezer gebruikt de ontdooidatum en berekent voorlopig 3 dagen houdbaarheid. Het etiket toont ONTDOOID en 'Na ontdooien gekoeld bewaren'." },
    { titel: "Bewaaradvies op een etiket", tekst:
"Het normale bewaaradvies wordt vastgelegd bij het MEP-item of gekoppelde recept. Bij Koeling wordt dat advies gebruikt. Kies je Vriezer of Uit vriezer, dan gebruikt KitchenControl alleen voor dat etiket het passende vries- of ontdooiadvies. Het opgeslagen item of recept wordt daarbij niet gewijzigd." },
    { titel: "Datum of THT handmatig aanpassen", tekst:
"Normaal berekent KitchenControl de datums automatisch. Gebruik Datum/THT aanpassen alleen wanneer je voor een uitzondering de datum of de gekoelde THT-termijn handmatig wilt wijzigen." },
    { titel: "Los etiket printen", tekst:
"Gebruik Los etiket voor een product dat niet als MEP-item in de lijst staat. Vul de productnaam en bewaartermijn in en ga daarna door naar dezelfde printflow." },
    { titel: "Printer offline", tekst:
"Controleer of de Brother-labelprinter en het KitchenControl-printkastje zijn ingeschakeld. Staat de printerstatus in KitchenControl op offline, controleer dan ook de netwerkverbinding van het printkastje. Blijft de printer offline, neem dan contact op met KitchenControl." },
  ]},

  { cat: "Whiteboard", artikelen: [
    { titel: "Waarvoor gebruik je het Whiteboard?", tekst:
"Het Whiteboard is de centrale lijst voor artikelen die aandacht nodig hebben. Je kunt zelf een regel toevoegen als iets Op of Bijna op is. Daarnaast kunnen bestelartikelen uit geplande MEP-items automatisch op het Whiteboard verschijnen." },
    { titel: "Een artikel handmatig toevoegen", tekst:
"Open het Whiteboard, voeg het artikel toe en kies de prioriteit Op of Bijna op. Iedereen die dezelfde bedrijfsomgeving gebruikt ziet de bijgewerkte lijst." },
    { titel: "MEP-regels op het Whiteboard", tekst:
"Bestelartikelen uit geplande MEP-items verschijnen automatisch op het Whiteboard. Daarbij kan zichtbaar zijn via welk MEP-item de regel is ontstaan. Zo hoef je geen aparte bestelbon bij te houden." },
    { titel: "Geregeld", tekst:
"Kies Geregeld wanneer een artikel is afgehandeld. Bij een handmatig Whiteboard-item wordt de regel verwijderd. Bij een regel die uit MEP komt wordt alleen die bestelbehoefte voor de betreffende week als geregeld gemarkeerd; de MEP-taak zelf blijft ongewijzigd." },
    { titel: "Whiteboard aanpassen en printen", tekst:
"Gebruik Aanpassen om een Whiteboard-regel voor het werkoverzicht te wijzigen. Met Printen maak je een compacte afdruk van de open regels. Op de afdruk wordt de interne bron/verwijzing van MEP-regels niet getoond." },
  ]},

  { cat: "Recepten", artikelen: [
    { titel: "Recepten bekijken", tekst:
"Open Recepten om door de recepten van je bedrijf te bladeren. Een recept kun je bekijken zonder beheer-pincode." },
    { titel: "Een recept toevoegen", tekst:
"Je kunt een recept op twee manieren toevoegen. Kies Nieuw recept om naam, ingrediënten, bereiding en andere gegevens stap voor stap zelf in te vullen. Heb je een bestaand recept als tekst, kies dan Recept plakken: plak de volledige tekst en laat KitchenControl die automatisch omzetten naar een net recept dat je daarna kunt controleren en opslaan. Toevoegen en wijzigen zijn beheerfuncties." },
    { titel: "Recept plakken", tekst:
"Gebruik Recept plakken wanneer je al een recept als tekst hebt, bijvoorbeeld uit een document, e-mail of website. Plak de tekst in het invoerveld. KitchenControl zet de inhoud automatisch om naar een receptopzet. Controleer daarna altijd de naam, ingrediënten, hoeveelheden, bereiding, allergenen, bewaaradvies en houdbaarheid voordat je het recept opslaat." },
    { titel: "Allergenen, bewaaradvies en houdbaarheid", tekst:
"Bij een recept kun je allergenen, bewaaradvies en houdbaarheid vastleggen. Als een MEP-item aan dit recept is gekoppeld, gebruikt de etiketprint deze informatie van het recept." },
    { titel: "Een recept koppelen aan MEP", tekst:
"Open het MEP-item via het potlood en kies het bijbehorende recept. Na opslaan gebruikt het MEP-item de receptinformatie waar dat van toepassing is. Het wijzigen van de koppeling is een beheerfunctie." },
    { titel: "Een recept printen", tekst:
"Open het recept en gebruik de printfunctie voor een nette A4-weergave. Recepten bekijken en printen zijn dagelijkse handelingen en vragen geen beheer-pincode." },
  ]},

  { cat: "HACCP", artikelen: [
    { titel: "Dagelijkse registraties", tekst:
"Dagelijkse HACCP-registraties, zoals temperatuur, ontvangst, schoonmaak, terugkoelen en afwijkingen, kunnen zonder beheer-pincode worden uitgevoerd. De pincode is bedoeld voor het wijzigen van de inrichting en instellingen." },
    { titel: "Temperatuur registreren", tekst:
"Open HACCP → Temperatuur, kies wat je meet, voer de gemeten temperatuur in en sla de registratie op. Gebruik Beheren binnen dit onderdeel om meetpunten of de inrichting te wijzigen; daarvoor is beheer nodig." },
    { titel: "Goederenontvangst registreren", tekst:
"Open HACCP → Ontvangst, kies of noteer de levering, voer de controle uit en sla op. Leveranciers en andere inrichting beheer je via Beheren binnen het onderdeel." },
    { titel: "Schoonmaak registreren", tekst:
"Open HACCP → Schoonmaak en vink de uitgevoerde taken af. Gebruik Beheren om de schoonmaaktaken zelf toe te voegen, aan te passen of te verwijderen." },
    { titel: "Terugkoelen registreren", tekst:
"Open HACCP → Terugkoelen en kies Nieuwe terugkoeling. Vul product, starttemperatuur, medewerker en eventueel thermometer in. De starttijd wordt automatisch vastgelegd. Na 2 uur verschijnt een tussencontrole; na 5 uur blijft een ontbrekende eindmeting zichtbaar. Rond zelf af met een gemeten eindtemperatuur. Herinneringen zijn zichtbaar binnen het geopende HACCP-scherm." },
    { titel: "Een afwijking vastleggen", tekst:
"Open HACCP → Afwijkingen. Beschrijf wat er is afgeweken en welke corrigerende maatregel is genomen en sla de registratie op." },
    { titel: "HACCP-instellingen beheren", tekst:
"De oude aparte HACCP-instellingenpagina is vereenvoudigd. Bij onderdelen zoals Schoonmaak, Temperatuur en Ontvangst vind je de relevante instellingen achter Beheren. Dat gedeelte is standaard ingeklapt en is een beheerfunctie." },
  ]},

  { cat: "HACCP-basiskennis", artikelen: [
    { titel: "Let op: dit zijn richtlijnen", tekst:
"De informatie in deze hulp is algemene ondersteuning en geen vervanging van de geldende Hygiënecode. Raadpleeg altijd de Hygiënecode die voor je bedrijf geldt en de informatie van de NVWA." },
    { titel: "Wat is HACCP en de Hygiënecode?", tekst:
"HACCP is een werkwijze om voedselveiligheidsrisico's te herkennen en beheersen. In Nederland wordt in veel horecabedrijven gewerkt volgens een goedgekeurde Hygiënecode. KitchenControl helpt registraties en dagelijkse controles overzichtelijk vast te leggen." },
    { titel: "Bewaartemperaturen (richtlijn)", tekst:
"- Gekoeld bewaren: volg het bewaaradvies en je eigen Hygiënecode.\n- Diepvries: doorgaans -18 °C of lager.\n\nVoor specifieke producten en situaties geldt altijd de toepasselijke Hygiënecode." },
    { titel: "De 14 allergenen", tekst:
"Gluten, schaaldieren, eieren, vis, pinda's, soja, melk, noten, selderij, mosterd, sesamzaad, zwaveldioxide/sulfiet, lupine en weekdieren." },
    { titel: "Wat doe je bij een afwijking?", tekst:
"Neem bij een afwijking direct een passende corrigerende maatregel en leg zowel de afwijking als de maatregel vast in KitchenControl. Raadpleeg voor de juiste grenswaarden en maatregelen je Hygiënecode." },
  ]},

  { cat: "Team", artikelen: [
    { titel: "Medewerkers en werktijden", tekst:
"Medewerkers en werktijden staan samen in Team. Je hoeft niet meer tussen aparte hoofdonderdelen te wisselen. Vanuit dezelfde Team-pagina beheer je medewerkers en het weekrooster." },
    { titel: "Een medewerker toevoegen of wijzigen", tekst:
"Open Team en gebruik de knop om een medewerker toe te voegen. Bestaande medewerkers kun je vanuit dezelfde pagina wijzigen of verwijderen. Dit zijn beheerfuncties." },
    { titel: "Het weekrooster gebruiken", tekst:
"Kies de gewenste week en vul de werktijden per medewerker in. Het rooster berekent de uren op basis van de ingevulde begin- en eindtijden en pauzes." },
    { titel: "Medewerkers bij etiketten", tekst:
"De medewerkers uit Team worden gebruikt in de etiketprint. In de printflow kies je wie het product heeft gemaakt of verwerkt; die naam komt op het etiket." },
  ]},

  { cat: "Beheer-pincode", artikelen: [
    { titel: "Waarvoor is de beheer-pincode?", tekst:
"De beheer-pincode beschermt wijzigingen en verwijderingen op een gedeelde keukentablet. Dagelijkse handelingen zoals MEP-taken afvinken, etiketten printen en HACCP-registraties uitvoeren blijven zonder pincode beschikbaar." },
    { titel: "Beheer ontgrendelen", tekst:
"Wanneer je een beheeractie uitvoert vraagt KitchenControl om de 4-cijferige pincode. Na een juiste pincode blijft beheer 10 minuten ontgrendeld. Via Instellingen kun je beheer ook direct opnieuw vergrendelen." },
    { titel: "Pincode instellen, wijzigen of uitschakelen", tekst:
"De eigenaar kan via Instellingen → Beheer-pincode een pincode instellen, wijzigen of uitschakelen. De pincode zelf wordt niet leesbaar opgeslagen." },
    { titel: "Pincode vergeten", tekst:
"Als de eigenaar de beheer-pincode niet meer weet, open dan een beheeractie zodat het pincodevenster verschijnt en kies Pincode vergeten?. KitchenControl stuurt een tijdelijke herstel-link naar het e-mailadres van het ingelogde eigenaar-account. Via die link kan een nieuwe 4-cijferige pincode worden gekozen. De oude pincode wordt nooit getoond." },
  ]},

  { cat: "Account & toegang", artikelen: [
    { titel: "Je account", tekst:
"Onder Instellingen → Account zie je het e-mailadres, de rol en het bedrijf van het ingelogde account. Je kunt er ook je schermnaam aanpassen." },
    { titel: "Wachtwoord wijzigen of vergeten", tekst:
"Ben je ingelogd, dan kun je onder Instellingen → Account een nieuw wachtwoord instellen. Ben je je wachtwoord vergeten, gebruik dan de hersteloptie op het inlogscherm en volg de link uit de e-mail." },
    { titel: "Collega's uitnodigen", tekst:
"De eigenaar kan onder Instellingen gebruikers uitnodigen voor de bedrijfsomgeving. De collega ontvangt een inloglink en krijgt de gekozen rol binnen het bedrijf." },
    { titel: "Rollen", tekst:
"Eigenaar/beheerder kan de bedrijfsomgeving volledig beheren. Een manager kan dagelijkse en veel beheerhandelingen uitvoeren, maar heeft niet dezelfde accountrechten als de eigenaar. Een medewerker is gericht op dagelijks keukenwerk en krijgt geen toegang tot beheertaken waarvoor de beheer-pincode of hogere rechten nodig zijn." },
    { titel: "Toegang intrekken", tekst:
"De eigenaar kan in Instellingen de toegang van een collega tot het bedrijf intrekken. Dit staat los van het verwijderen van een medewerker uit de Team-lijst." },
  ]},

  { cat: "Problemen oplossen", artikelen: [
    { titel: "Een ander apparaat toont nog oude gegevens", tekst:
"KitchenControl synchroniseert gegevens automatisch, maar een reeds geopend scherm kan soms nog een oudere versie tonen. Ververs de pagina. Blijft de informatie achter, log dan uit en opnieuw in zodat de actuele bedrijfsconfig opnieuw wordt geladen." },
    { titel: "De beheer-pincode lijkt op één apparaat niet te kloppen", tekst:
"Controleer eerst of de pincode op een ander apparaat recent is gewijzigd of uitgeschakeld. Ververs de pagina op het apparaat dat achterloopt. De beheer-pincode hoort bij de bedrijfsconfig en niet bij één losse tablet of computer." },
    { titel: "De printer doet het niet", tekst:
"Controleer of printer en printkastje voeding hebben en of de printerstatus in het etiketscherm online is. Staat de printer offline, controleer dan de verbinding van het printkastje. Blijft het probleem bestaan, neem dan contact op met KitchenControl." },
    { titel: "Werkt KitchenControl op telefoon en tablet?", tekst:
"Ja. KitchenControl draait in een moderne browser op telefoon, tablet en computer. Voor dagelijks keukenwerk is een tablet vaak het meest praktisch." },
  ]},
];

const C = {
  navy:"#0F172A", ink:"#14213D", soft:"#51607C", mut:"#8A93A6",
  blue:"#2563EB", blueBg:"#EFF6FF", line:"#E5E9F0", bg:"#FFFFFF", bg2:"#F4F6FA",
};

// ── Het "?"-knopje voor de bovenbalk ─────────────────────────────────────────
export function HelpButton({ onClick, compact }) {
  const [h, setH] = useState(false);
  if (compact) {
    return (
      <button onClick={onClick} title="Hulp" aria-label="Hulp"
        onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
        style={{ width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          background: "#fff", border: `1px solid ${h ? C.blue : C.line}`, cursor: "pointer", padding: 0,
          boxShadow: "0 2px 8px rgba(15,23,42,.12)", transition: "border-color .12s" }}>
        <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: h ? C.blue : C.soft }}>?</span>
      </button>
    );
  }
  return (
    <button onClick={onClick} title="Hulp"
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: h ? C.bg2 : "none",
        border: `1px solid ${C.line}`, color: C.ink, cursor: "pointer", fontSize: 12.5, fontWeight: 600,
        fontFamily: "inherit", padding: "7px 13px", borderRadius: 8, transition: "background .12s" }}>
      <span style={{ fontSize: 15, fontWeight: 800, lineHeight: 1 }}>?</span> Hulp
    </button>
  );
}

const HELP_TOPIC_CATS = {
  mep: ["Mise en place", "Etiketten printen"],
  haccp: ["HACCP", "HACCP-basiskennis"],
  recepten: ["Recepten"],
  team: ["Team"],
  whiteboard: ["Whiteboard"],
  settings: ["Beheer-pincode", "Account & toegang", "Problemen oplossen"],
};

// ── Het hulp-paneel (zoeken + bladeren + artikel lezen) ───────────────────────
export function HelpPanel({ open, onClose, startTopic = null }) {
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(null); // gekozen artikel { cat, titel, tekst }
  const [toonAlles, setToonAlles] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(""); setSel(null); setToonAlles(false); return; }
    setQuery("");
    setSel(null);
    setToonAlles(false);
  }, [open, startTopic]);

  const topicCategorieen = startTopic ? HELP_TOPIC_CATS[startTopic] : null;
  const zichtbareGroepen = (!toonAlles && topicCategorieen?.length)
    ? HELP_CONTENT.filter((g) => topicCategorieen.includes(g.cat))
    : HELP_CONTENT;

  const treffers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const out = [];
    zichtbareGroepen.forEach(g => g.artikelen.forEach(a => {
      if (a.titel.toLowerCase().includes(q) || a.tekst.toLowerCase().includes(q)) out.push({ cat: g.cat, ...a });
    }));
    return out;
  }, [query, zichtbareGroepen]);

  if (!open) return null;

  const rij = (a, withCat) => (
    <button key={a.cat + a.titel} onClick={() => setSel(a)}
      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
        background: "none", border: "none", borderBottom: `1px solid ${C.line}`, cursor: "pointer",
        padding: "12px 4px", fontFamily: "inherit" }}
      onMouseEnter={e => e.currentTarget.style.background = C.bg2}
      onMouseLeave={e => e.currentTarget.style.background = "none"}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: C.ink }}>{a.titel}</span>
        {withCat && <span style={{ display: "block", fontSize: 11.5, color: C.mut, marginTop: 1 }}>{a.cat}</span>}
      </span>
      <span style={{ color: C.mut, fontSize: 18, lineHeight: 1 }}>›</span>
    </button>
  );

  return (
    <>
      <div onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.42)", zIndex: 60 }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(440px, 100%)", background: C.bg,
        zIndex: 61, display: "flex", flexDirection: "column", boxShadow: "-12px 0 40px -16px rgba(15,23,42,.4)",
        fontFamily: "system-ui,-apple-system,sans-serif" }}>

        {/* kop */}
        <div style={{ flexShrink: 0, padding: "16px 18px", borderBottom: `1px solid ${C.line}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 24, height: 24, borderRadius: "50%", background: C.blueBg, color: C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, flex: "none" }}>?</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>Hulp</div>
              <div style={{ fontSize: 11.5, color: C.mut }}>Antwoorden op veelgestelde vragen</div>
            </div>
          </div>
          <button onClick={onClose} title="Sluiten" aria-label="Sluiten"
            style={{ background: "none", border: "none", cursor: "pointer", color: C.soft, padding: 4, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        {sel ? (
          /* ── artikel lezen ── */
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px 28px" }}>
            <button onClick={() => setSel(null)}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none",
                cursor: "pointer", color: C.blue, fontSize: 13, fontWeight: 600, fontFamily: "inherit", padding: "4px 0 12px" }}>
              <ChevronLeft size={15} /> Alle onderwerpen
            </button>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: C.blue, marginBottom: 6 }}>{sel.cat}</div>
            <h2 style={{ fontSize: 19, fontWeight: 700, color: C.navy, margin: "0 0 12px", lineHeight: 1.25 }}>{sel.titel}</h2>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 14.5, lineHeight: 1.62, color: C.ink }}>{sel.tekst}</div>
          </div>
        ) : (
          <>
            {/* zoeken */}
            <div style={{ flexShrink: 0, padding: "12px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.bg2, border: `1px solid ${C.line}`, borderRadius: 10, padding: "9px 12px" }}>
                <Search size={16} color={C.mut} />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek in de hulp…"
                  autoComplete="off" autoCorrect="off" spellCheck={false}
                  style={{ flex: 1, border: "none", outline: "none", background: "none", fontFamily: "inherit", fontSize: 14.5, color: C.ink }} />
                {query && <button onClick={() => setQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: C.mut, padding: 2, display: "flex" }}><X size={15} /></button>}
              </div>
            </div>

            {/* lijst */}
            <div style={{ flex: 1, overflowY: "auto", padding: "0 18px 24px" }}>
              {!query && !toonAlles && topicCategorieen?.length > 0 && (
                <button onClick={() => setToonAlles(true)}
                  style={{ border: "none", background: "none", color: C.blue, padding: "4px 4px 8px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Alle hulp-onderwerpen
                </button>
              )}
              {treffers ? (
                treffers.length ? treffers.map(a => rij(a, true)) : (
                  <div style={{ padding: "26px 4px", color: C.mut, fontSize: 14 }}>Geen resultaten — probeer een ander woord.</div>
                )
              ) : (
                zichtbareGroepen.map(g => (
                  <div key={g.cat}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: C.mut, margin: "18px 4px 4px" }}>{g.cat}</div>
                    {g.artikelen.map(a => rij({ cat: g.cat, ...a }, false))}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default HelpPanel;
